/**
 * Center-focus product carousel: the card nearest the middle of the track is marked active,
 * and the dots follow it. Markup is rendered by sections/collection-carousel.liquid.
 */
class CollectionCarousel extends HTMLElement {
  #frame = 0;
  #activeIndex = 0;

  // Autoplay: advances while the carousel is on screen and nobody is interacting with it
  #autoplayTimer = 0;
  #resumeTimer = 0;
  #inView = false;
  #hovered = false;
  #userPaused = false;
  #observer = /** @type {IntersectionObserver | null} */ (null);

  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track]');
    this.cards = [...this.querySelectorAll('[data-carousel-card]')];
    this.dots = [...this.querySelectorAll('[data-carousel-dot]')];
    if (!this.track || this.cards.length === 0) return;

    this.track.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onScroll);

    this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.goTo(index)));

    // Clicking a side card brings it to the middle instead of opening it
    this.cards.forEach((card, index) => {
      card.addEventListener('click', (event) => {
        if (card.hasAttribute('data-active')) return;
        event.preventDefault();
        this.goTo(index);
      });
    });

    const start = Math.min(Number(this.dataset.startIndex) || 0, this.cards.length - 1);
    this.goTo(start, 'instant');
    this.#update();

    this.#setupAutoplay();
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onScroll);
    document.removeEventListener('visibilitychange', this.#syncAutoplay);
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
    clearInterval(this.#autoplayTimer);
    clearTimeout(this.#resumeTimer);
  }

  get #autoplayDelay() {
    return Number(this.dataset.autoplay) * 1000;
  }

  #setupAutoplay() {
    if (!this.#autoplayDelay || this.cards.length < 2) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // Any direct interaction pauses autoplay; it resumes after a few idle seconds
    for (const type of ['pointerdown', 'touchstart', 'wheel', 'keydown', 'focusin']) {
      this.addEventListener(type, this.#onUserInteraction, { passive: true });
    }

    this.addEventListener('pointerenter', (event) => {
      if (event.pointerType !== 'mouse') return;
      this.#hovered = true;
      this.#syncAutoplay();
    });
    this.addEventListener('pointerleave', (event) => {
      if (event.pointerType !== 'mouse') return;
      this.#hovered = false;
      this.#syncAutoplay();
    });

    this.#observer = new IntersectionObserver(
      ([entry]) => {
        this.#inView = Boolean(entry?.isIntersecting);
        this.#syncAutoplay();
      },
      { threshold: 0.5 }
    );
    this.#observer.observe(this);

    document.addEventListener('visibilitychange', this.#syncAutoplay);
  }

  #onUserInteraction = () => {
    this.#userPaused = true;
    this.#syncAutoplay();

    clearTimeout(this.#resumeTimer);
    this.#resumeTimer = window.setTimeout(() => {
      this.#userPaused = false;
      this.#syncAutoplay();
    }, Math.max(this.#autoplayDelay, 5000));
  };

  #syncAutoplay = () => {
    const shouldRun = this.#inView && !this.#hovered && !this.#userPaused && !document.hidden;

    if (shouldRun && !this.#autoplayTimer) {
      this.#autoplayTimer = window.setInterval(() => {
        this.goTo((this.#activeIndex + 1) % this.cards.length);
      }, this.#autoplayDelay);
    } else if (!shouldRun && this.#autoplayTimer) {
      clearInterval(this.#autoplayTimer);
      this.#autoplayTimer = 0;
    }
  };

  /**
   * @param {number} index
   * @param {ScrollBehavior} [behavior]
   */
  goTo(index, behavior = 'smooth') {
    const card = this.cards[index];
    if (!card || !this.track) return;

    // Measure from the card's on-screen centre: offsetLeft is relative to the section, which sits
    // wider than the track on desktop, and the transform scale doesn't move a card's centre.
    const trackBox = this.track.getBoundingClientRect();
    const cardBox = card.getBoundingClientRect();
    const cardCentre = cardBox.left + cardBox.width / 2 - trackBox.left;
    const left = this.track.scrollLeft + cardCentre - this.track.clientWidth / 2;
    this.track.scrollTo({ left, behavior });
  }

  #onScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  };

  #update() {
    if (!this.track) return;

    const trackBox = this.track.getBoundingClientRect();
    const middle = trackBox.left + trackBox.width / 2;
    let activeIndex = 0;
    let closest = Infinity;

    this.cards.forEach((card, index) => {
      const box = card.getBoundingClientRect();
      const distance = Math.abs(box.left + box.width / 2 - middle);
      if (distance < closest) {
        closest = distance;
        activeIndex = index;
      }
    });

    this.#activeIndex = activeIndex;

    this.cards.forEach((card, index) => {
      const active = index === activeIndex;
      card.toggleAttribute('data-active', active);
      card.querySelector('a')?.setAttribute('tabindex', active ? '0' : '-1');
    });

    this.dots.forEach((dot, index) => dot.setAttribute('aria-current', String(index === activeIndex)));
  }
}

if (!customElements.get('collection-carousel')) {
  customElements.define('collection-carousel', CollectionCarousel);
}
